/**
 * Holiday Types for MEV-Med Frontend
 * Type definitions for Holiday management
 *
 * @version 2025.1.0
 * @description Tipos TypeScript para gestão de Feriados
 */

/**
 * Holiday type enumeration
 */
export enum HolidayType {
  NACIONAL = 'NACIONAL',
  ESTADUAL = 'ESTADUAL',
  MUNICIPAL = 'MUNICIPAL',
  EMPRESA = 'EMPRESA'
}

/**
 * Holiday type labels in Portuguese
 */
export const HOLIDAY_TYPE_LABELS: Record<HolidayType, string> = {
  [HolidayType.NACIONAL]: 'Nacional',
  [HolidayType.ESTADUAL]: 'Estadual',
  [HolidayType.MUNICIPAL]: 'Municipal',
  [HolidayType.EMPRESA]: 'Empresa'
};

/**
 * Holiday type colors for UI
 */
export const HOLIDAY_TYPE_COLORS: Record<HolidayType, 'primary' | 'secondary' | 'info' | 'warning'> = {
  [HolidayType.NACIONAL]: 'primary',
  [HolidayType.ESTADUAL]: 'secondary',
  [HolidayType.MUNICIPAL]: 'info',
  [HolidayType.EMPRESA]: 'warning'
};

/**
 * List of valid Brazilian UFs
 */
export const UF_OPTIONS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS',
  'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC',
  'SP', 'SE', 'TO'
] as const;

// Note: BrazilianState type is exported from common.ts

/**
 * Holiday entity interface
 */
export interface Holiday {
  id: number;
  date: string;
  name: string;
  type: HolidayType;
  state?: string;
  city?: string;
  companyId?: number;
  companyName?: string;
  isRecurring: boolean;
  isActive: boolean;
  formattedDate?: string;
  typeDisplayName?: string;
  scopeDescription?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Form data for creating/updating holidays
 */
export interface HolidayFormData {
  date: string;
  name: string;
  type: HolidayType;
  state?: string;
  city?: string;
  companyId?: number;
  isRecurring?: boolean;
}

/**
 * Filters for listing holidays
 */
export interface HolidayFilters {
  year?: number;
  type?: HolidayType;
  state?: string;
  city?: string;
  companyId?: number;
  isActive?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

/**
 * Response for list endpoint with pagination
 */
export interface HolidayListResponse {
  holidays: Holiday[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Holiday check result
 */
export interface HolidayCheckResult {
  isHoliday: boolean;
  holidays: Holiday[];
}

/**
 * Import holidays result
 */
export interface HolidayImportResult {
  imported: number;
  skipped: number;
  message: string;
}

/**
 * Props for UI components
 */
export interface HolidayTableProps {
  holidays: Holiday[];
  onEdit: (holiday: Holiday) => void;
  onDelete: (id: number) => void;
  loading?: boolean;
}

export interface HolidayFormProps {
  initialData?: Holiday;
  onSubmit: (data: HolidayFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

export interface HolidayFiltersProps {
  filters: HolidayFilters;
  onChange: (filters: HolidayFilters) => void;
  onSearch: () => void;
}

/**
 * Helper function to format holiday date for display
 */
export function formatHolidayDate(dateString: string): string {
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
}

/**
 * Helper function to get scope description
 */
export function getHolidayScopeDescription(holiday: Holiday): string {
  switch (holiday.type) {
    case HolidayType.NACIONAL:
      return 'Nacional';
    case HolidayType.ESTADUAL:
      return `Estadual (${holiday.state ?? 'N/A'})`;
    case HolidayType.MUNICIPAL:
      return `Municipal (${holiday.city ?? 'N/A'}/${holiday.state ?? 'N/A'})`;
    case HolidayType.EMPRESA:
      return `Empresa (${holiday.companyName ?? 'N/A'})`;
    default:
      return 'Desconhecido';
  }
}

/**
 * Helper function to get available years for selection
 */
export function getAvailableYears(): number[] {
  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let year = currentYear - 1; year <= currentYear + 2; year++) {
    years.push(year);
  }
  return years;
}

/**
 * Helper function to check if holiday type requires state
 */
export function requiresState(type: HolidayType): boolean {
  return type === HolidayType.ESTADUAL || type === HolidayType.MUNICIPAL;
}

/**
 * Helper function to check if holiday type requires city
 */
export function requiresCity(type: HolidayType): boolean {
  return type === HolidayType.MUNICIPAL;
}

/**
 * Helper function to check if holiday type requires company
 */
export function requiresCompany(type: HolidayType): boolean {
  return type === HolidayType.EMPRESA;
}
